import { Box } from '@mui/material';
import { DemographicChartProps } from '../../../types/reports';

import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  ReferenceArea,
  Scatter,
} from 'recharts';
import { findPercentile, getNasemScoreConfig, getSelectedData } from '../../../utils/reports';
import { chartConfigurations, ChartConfig } from '../../../configs/comparisonChartConfig';
import ReportNasemScoreBadgeAndText from '../../../reports/ReportNasemScoreBadgeAndText';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

const DemographicChart: React.FC<DemographicChartProps> = ({
  reportDemographicData,
  nasemScore,
  selectedFilter,
  dataset,
  hasAboveRange,
}) => {
  const { theme } = useThemeContext();

  const renderCustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const isSpecialTick = payload.value === 2 || payload.value === 20;

    return (
      <text
        x={x}
        y={y + 15}
        textAnchor="middle"
        fontSize={12}
        fontWeight="bold"
        fill={
          isSpecialTick
            ? payload.value === 2
              ? theme.custom.report.comparison.renderCustomXAxisTick.specialTick.twoColor
              : theme.custom.report.comparison.renderCustomXAxisTick.specialTick.twentyColor
            : theme.custom.report.comparison.renderCustomXAxisTick.color
        }
      >
        {payload.value}
      </text>
    );
  };

  const renderComposedChart = () => {
    const data = getSelectedData(reportDemographicData, dataset, selectedFilter);
    if (data) {
      const referenceX = nasemScore;
      const referenceY = findPercentile(nasemScore, data);

      // Retrieve configuration based on dataset and filter
      const config: ChartConfig = chartConfigurations[dataset]?.[selectedFilter] || {
        xDomain: [0, 120],
        yDomain: [0, 100],
        xTicks: [0, 20, 40, 60, 80, 100],
      };

      const { exposureTextColor, badgeBackgroundColor } = getNasemScoreConfig(nasemScore, theme);

      return (
        <Box>
          <ReportNasemScoreBadgeAndText
            nasemScore={nasemScore}
            userPercentile={referenceY}
            showBadge={true}
            hasAboveRange={hasAboveRange}
          />
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="" stroke={theme.custom.report.comparison.composedChart.gridColor} />
              <XAxis
                dataKey="x"
                type="number"
                domain={config.xDomain}
                tick={renderCustomXAxisTick}
                tickLine={false}
                axisLine={{ stroke: theme.custom.report.comparison.composedChart.xAxis.axisLine.stroke }}
                ticks={config.xTicks}
                tickFormatter={(value) => String(Math.round(value))}
              />
              <YAxis
                dataKey="y"
                type="number"
                domain={config.yDomain}
                tick={{ fill: theme.custom.report.comparison.composedChart.yAxis.tick.fill, fontSize: 12 }}
                axisLine={{ stroke: theme.custom.report.comparison.composedChart.yAxis.axisLine.stroke }}
                tickLine={{ stroke: theme.custom.report.comparison.composedChart.yAxis.tickLine.stroke }}
                tickFormatter={(value) => `${value}%`}
              />
              {/* Shading */}
              {nasemScore < 2 && (
                <ReferenceArea
                  x1={0}
                  x2={2}
                  fill={theme.custom.report.comparison.composedChart.area.low.fill}
                  fillOpacity={0.2}
                />
              )}
              {nasemScore >= 2 && nasemScore <= 20 && (
                <ReferenceArea
                  x1={2}
                  x2={20}
                  fill={theme.custom.report.comparison.composedChart.area.medium.fill}
                  fillOpacity={0.2}
                />
              )}
              {nasemScore > 20 && (
                <ReferenceArea
                  x1={20}
                  x2={config.xDomain[1]}
                  fill={theme.custom.report.comparison.composedChart.area.high.fill}
                  fillOpacity={0.2}
                />
              )}
              <Line
                type="monotone"
                dataKey="y"
                stroke={theme.custom.report.comparison.composedChart.line.stroke}
                strokeWidth={4}
                dot={false}
              />
              {/* Add Reference Lines */}
              {/* Horizontal line */}
              <ReferenceLine
                segment={[
                  {
                    x: 0,
                    y: referenceY,
                  },
                  {
                    x: referenceX,
                    y: referenceY,
                  },
                ]}
                stroke={exposureTextColor}
                strokeDasharray="3 3"
                strokeWidth={2}
                ifOverflow="extendDomain"
                isFront
                label={{
                  value: `${referenceY}%`,
                  position: 'left',
                  fill: exposureTextColor,
                  fontSize: 12,
                }}
              />

              {/* vertical line */}
              <ReferenceLine
                segment={[
                  {
                    x: referenceX,
                    y: 0,
                  },
                  {
                    x: referenceX,
                    y: referenceY,
                  },
                ]}
                stroke={exposureTextColor}
                strokeDasharray="3 3"
                strokeWidth={2}
                ifOverflow="extendDomain"
                isFront
                label={{
                  value: `${referenceX}`,
                  position: 'bottom',
                  fill: exposureTextColor,
                  fontSize: 12,
                  offset: 3,
                }}
              />

              {/* Adds the circle in the horizontal and vertical line intersection */}
              <Scatter
                data={[{ x: referenceX, y: referenceY }]}
                fill={badgeBackgroundColor}
                stroke={exposureTextColor}
                strokeWidth={2}
                shape="circle"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      );
    }
  };

  return <Box>{renderComposedChart()}</Box>;
};

export default DemographicChart;
